import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { Loader2, Shield, UserCog, AlertTriangle, User, ScanFace, Eye, EyeOff, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  isBiometricAvailable,
  hasStoredCredentials,
  saveCredentials,
  deleteCredentials,
  isNativeIOS,
} from "@/lib/biometric";

export default function ProfilePage() {
  const { user, refreshMe, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  // Notification preferences
  const [emailNotifOptIn, setEmailNotifOptIn] = useState(
    user?.notification_email_opt_in !== false
  );
  const [savingEmailPref, setSavingEmailPref] = useState(false);

  useEffect(() => {
    setEmailNotifOptIn(user?.notification_email_opt_in !== false);
  }, [user?.notification_email_opt_in]);

  const onToggleEmailNotif = async (nextValue) => {
    setSavingEmailPref(true);
    setEmailNotifOptIn(nextValue);
    try {
      await api.patch("/auth/me/notification-preferences", {
        notification_email_opt_in: nextValue,
      });
      await refreshMe?.();
      toast.success(
        nextValue
          ? "You'll now receive email notifications"
          : "Email notifications turned off"
      );
    } catch (err) {
      setEmailNotifOptIn(!nextValue);
      toast.error(formatApiError(err, "Could not save preference"));
    } finally {
      setSavingEmailPref(false);
    }
  };

  // Account deletion state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const onDeleteAccount = async () => {
    if (deleteConfirm.trim().toUpperCase() !== "DELETE") {
      toast.error('Please type DELETE to confirm');
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/auth/me");
      // Clear any locally stored biometric credentials too
      try {
        await deleteCredentials();
      } catch (e) { /* non-fatal */ }
      localStorage.removeItem("byrd_biometric_enrolled");
      toast.success("Your account has been deleted");
      setDeleteOpen(false);
      // Small delay so toast is visible, then sign out + redirect to login
      setTimeout(() => {
        logout?.();
      }, 600);
    } catch (err) {
      toast.error(formatApiError(err, "Could not delete account"));
      setDeleting(false);
    }
  };

  // Biometric (Face ID / Touch ID) state for native iOS only.
  const [bioAvailable, setBioAvailable] = useState(false);
  const [bioType, setBioType] = useState(null);
  const [bioEnrolled, setBioEnrolled] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isNativeIOS()) return;
      const avail = await isBiometricAvailable();
      if (cancelled) return;
      setBioAvailable(avail.available);
      setBioType(avail.type);
      if (avail.available) {
        const has = await hasStoredCredentials();
        if (!cancelled) setBioEnrolled(has);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const bioLabel = bioType === 1 ? "Touch ID" : "Face ID";

  const onDisableBiometric = async () => {
    setBioBusy(true);
    try {
      await deleteCredentials();
      localStorage.removeItem("byrd_biometric_enrolled");
      setBioEnrolled(false);
      toast.success(`${bioLabel} disabled on this device`);
    } finally {
      setBioBusy(false);
    }
  };

  const onResetBiometric = async () => {
    // Full reset: clear keychain (if any), persistent enrollment flag, and
    // session dismissal flag. Next successful password login will re-prompt.
    setBioBusy(true);
    try {
      try {
        await deleteCredentials();
      } catch {
        /* keychain may already be empty */
      }
      localStorage.removeItem("byrd_biometric_enrolled");
      sessionStorage.removeItem("byrd_biometric_dismissed_session");
      setBioEnrolled(false);
      setBioShowEnroll(false);
      setBioEnrollPwd("");
      toast.success(`${bioLabel} enrollment reset · sign out and back in to re-enable`);
    } finally {
      setBioBusy(false);
    }
  };

  const [bioEnrollPwd, setBioEnrollPwd] = useState("");
  const [bioShowEnroll, setBioShowEnroll] = useState(false);

  const onEnableBiometric = async () => {
    if (!user?.email || !bioEnrollPwd) return;
    setBioBusy(true);
    try {
      // Verify the password by attempting a login (without disturbing the
      // current session — we just need the server to confirm credentials are
      // valid before we drop them into the keychain).
      try {
        await api.post("/auth/login", {
          email: user.email,
          password: bioEnrollPwd,
        });
      } catch {
        toast.error("Password didn't match. Try again.");
        return;
      }
      const ok = await saveCredentials(user.email, bioEnrollPwd);
      if (ok) {
        localStorage.setItem("byrd_biometric_enrolled", "1");
        setBioEnrolled(true);
        setBioEnrollPwd("");
        setBioShowEnroll(false);
        toast.success(`${bioLabel} enabled — next sign-in will offer it`);
      } else {
        toast.error("Could not save to Keychain. Try again.");
      }
    } finally {
      setBioBusy(false);
    }
  };

  const mustChange = user?.must_change_password;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pw1.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (pw1 !== pw2) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: pw1,
      });
      toast.success("Password updated");
      setCurrent("");
      setPw1("");
      setPw2("");
      refreshMe?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setSavingName(true);
    try {
      await api.patch("/auth/me", { name });
      toast.success("Name updated");
      refreshMe?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingName(false);
    }
  };

  const roleLabel =
    user?.role === "agency_user"
      ? "Agency user"
      : user?.role === "distributor"
      ? "Distributor"
      : user?.role;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
          Profile
        </div>
        <h1 className="mt-1 text-3xl font-display font-bold tracking-tight text-slate-900">
          {user?.name}
        </h1>
      </div>

      {mustChange && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-sm p-4 flex items-start gap-3"
          data-testid="must-change-banner"
        >
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-amber-900">
              Please set a new password
            </div>
            <p className="text-xs text-amber-800 mt-0.5">
              You're signed in with a temporary password. Change it now to
              secure your account.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white border border-border rounded-sm p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
            Email
          </div>
          <div className="mt-1 font-mono-data">{user?.email}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
            Role
          </div>
          <div className="mt-1 inline-flex items-center gap-1.5 font-medium">
            {user?.role === "admin" ? (
              <Shield className="w-3.5 h-3.5 text-amber-600" />
            ) : user?.role === "distributor" ? (
              <User className="w-3.5 h-3.5 text-slate-600" />
            ) : (
              <UserCog className="w-3.5 h-3.5" />
            )}
            {roleLabel}
          </div>
        </div>
        {user?.role === "distributor" && (
          <>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
                Distributor
              </div>
              <div className="mt-1">{user?.distributor_name || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
                Location
              </div>
              <div className="mt-1">{user?.distributor_location || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
                Phone
              </div>
              <div className="mt-1 font-mono-data">{user?.phone || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
                Rep agency
              </div>
              <div className="mt-1">{user?.agency_name || "—"}</div>
            </div>
          </>
        )}
      </div>

      <div className="bg-white border border-border rounded-sm p-6">
        <h2 className="font-display text-lg font-semibold tracking-tight">
          Display name
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          How you appear in the app.
        </p>
        <form
          onSubmit={saveName}
          className="mt-4 flex gap-2 items-end"
          data-testid="update-name-form"
        >
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-wider">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5 rounded-sm"
              data-testid="profile-name-input"
            />
          </div>
          <Button
            type="submit"
            disabled={savingName || name === user?.name}
            className="rounded-sm bg-slate-900 hover:bg-slate-800"
            data-testid="profile-name-save"
          >
            {savingName && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </form>
      </div>

      <div className="bg-white border border-border rounded-sm p-6" data-testid="notification-preferences-card">
        <h2 className="font-display text-lg font-semibold tracking-tight">
          Notification preferences
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Choose how you want to hear from your rep agency. In-app inbox
          notifications are always on.
        </p>
        <div className="mt-5 flex items-start justify-between gap-4 p-4 border border-border rounded-sm bg-slate-50/50">
          <div className="flex-1">
            <div className="font-medium text-sm text-slate-900">
              Email notifications
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Get a copy of important updates by email. Useful if you don't have
              notifications turned on at the phone level.
            </div>
          </div>
          <label
            className="relative inline-flex items-center cursor-pointer shrink-0"
            data-testid="email-notif-toggle"
          >
            <input
              type="checkbox"
              checked={emailNotifOptIn}
              onChange={(e) => onToggleEmailNotif(e.target.checked)}
              disabled={savingEmailPref}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-slate-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-slate-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900"></div>
          </label>
        </div>
      </div>

      <div className="bg-white border border-border rounded-sm p-6">
        <h2 className="font-display text-lg font-semibold tracking-tight">
          Change password
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Update your sign-in password. You will stay signed in on this device.
        </p>
        <form
          onSubmit={onSubmit}
          className="mt-5 space-y-4"
          data-testid="change-password-form"
        >
          <div>
            <Label className="text-xs uppercase tracking-wider">
              Current password
            </Label>
            <div className="relative mt-1.5">
              <Input
                type={showCurrent ? "text" : "password"}
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="rounded-sm pr-10"
                data-testid="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                aria-label={showCurrent ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-700"
                data-testid="current-password-toggle"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">
                New password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  type={showPw1 ? "text" : "password"}
                  required
                  minLength={6}
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  className="rounded-sm pr-10"
                  data-testid="new-password-1"
                />
                <button
                  type="button"
                  onClick={() => setShowPw1((v) => !v)}
                  aria-label={showPw1 ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-700"
                  data-testid="new-password-1-toggle"
                  tabIndex={-1}
                >
                  {showPw1 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">
                Confirm new password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  type={showPw2 ? "text" : "password"}
                  required
                  minLength={6}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="rounded-sm pr-10"
                  data-testid="new-password-2"
                />
                <button
                  type="button"
                  onClick={() => setShowPw2((v) => !v)}
                  aria-label={showPw2 ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-700"
                  data-testid="new-password-2-toggle"
                  tabIndex={-1}
                >
                  {showPw2 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <div>
            <Button
              type="submit"
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              data-testid="change-password-submit"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update password
            </Button>
          </div>
        </form>
      </div>

      {bioAvailable && (
        <div className="bg-white border border-border rounded-sm p-6" data-testid="biometric-settings">
          <h2 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2">
            <ScanFace className="w-4 h-4" />
            {bioLabel} sign-in
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {bioEnrolled
              ? `${bioLabel} is enabled on this device. Your credentials are stored securely in the iOS Keychain.`
              : `Enable ${bioLabel} to skip typing your password on this device.`}
          </p>
          {bioEnrolled ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={onDisableBiometric}
                disabled={bioBusy}
                className="rounded-sm"
                data-testid="biometric-disable"
              >
                {bioBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Disable {bioLabel} on this device
              </Button>
              <Button
                variant="ghost"
                onClick={onResetBiometric}
                disabled={bioBusy}
                className="rounded-sm text-slate-600"
                data-testid="biometric-reset"
              >
                Reset {bioLabel} enrollment
              </Button>
            </div>
          ) : !bioShowEnroll ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                onClick={() => setBioShowEnroll(true)}
                disabled={bioBusy}
                className="rounded-sm"
                data-testid="biometric-enable"
              >
                <ScanFace className="w-4 h-4 mr-2" />
                Enable {bioLabel}
              </Button>
              <Button
                variant="ghost"
                onClick={onResetBiometric}
                disabled={bioBusy}
                className="rounded-sm text-slate-600"
                data-testid="biometric-reset"
              >
                Reset {bioLabel} enrollment
              </Button>
            </div>
          ) : (
            <div className="mt-4 space-y-3 max-w-sm">
              <Label htmlFor="bio-enroll-pwd" className="text-xs uppercase tracking-wider font-mono-data text-muted-foreground">
                Confirm your password
              </Label>
              <Input
                id="bio-enroll-pwd"
                type="password"
                value={bioEnrollPwd}
                onChange={(e) => setBioEnrollPwd(e.target.value)}
                placeholder="Your current password"
                disabled={bioBusy}
                className="rounded-sm"
                data-testid="biometric-enroll-password"
              />
              <div className="flex items-center gap-2">
                <Button
                  onClick={onEnableBiometric}
                  disabled={bioBusy || !bioEnrollPwd}
                  className="rounded-sm"
                  data-testid="biometric-enroll-confirm"
                >
                  {bioBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save to Keychain
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setBioShowEnroll(false);
                    setBioEnrollPwd("");
                  }}
                  disabled={bioBusy}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Your password is encrypted and stored in the iOS Secure Enclave — it never leaves your device.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Danger Zone — Account deletion (required by Apple Guideline 5.1.1(v)) */}
      {user?.role !== "admin" && (
        <div
          className="bg-white border border-red-200 rounded-sm p-6"
          data-testid="danger-zone"
        >
          <h2 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2 text-red-700">
            <Trash2 className="w-4 h-4" />
            Delete account
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Permanently delete your My Rep Agency account and all associated
            data — your saved cart, quote history, and contact info. This action
            cannot be undone.
          </p>
          <div className="mt-4">
            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-sm text-red-700 border-red-300 hover:bg-red-50 hover:text-red-800"
                  data-testid="delete-account-btn"
                  onClick={() => setDeleteConfirm("")}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete my account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="delete-account-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Delete your account?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-3 pt-2">
                    <span className="block">
                      This will permanently delete your account
                      <span className="font-mono-data text-slate-900">
                        {" "}({user?.email}){" "}
                      </span>
                      and remove the following data from our servers:
                    </span>
                    <span className="block">
                      • Saved cart and item notes
                      <br />
                      • Quote request history
                      <br />
                      • Any pending registration with your rep agency
                      <br />
                      • Your profile (name, phone, distributor info)
                    </span>
                    <span className="block text-red-700 font-medium">
                      This cannot be undone. To proceed, type{" "}
                      <span className="font-mono-data">DELETE</span> below.
                    </span>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-2">
                  <Input
                    autoFocus
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder="Type DELETE to confirm"
                    className="rounded-sm font-mono-data"
                    data-testid="delete-confirm-input"
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel
                    disabled={deleting}
                    data-testid="delete-cancel-btn"
                  >
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDeleteAccount}
                    disabled={
                      deleting ||
                      deleteConfirm.trim().toUpperCase() !== "DELETE"
                    }
                    className="bg-red-700 hover:bg-red-800 focus:ring-red-700"
                    data-testid="delete-confirm-btn"
                  >
                    {deleting && (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    )}
                    Permanently delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}
    </div>
  );
}
